import React from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  styled,
  useTheme,
} from '@mui/material';
import { imageArray, Media } from '@askporter/utils';
import { RevampedIcon, RevampedIconSize } from '../../../RevampedIcon';
import { ThumbnailPreview } from '../../../ThumbnailPreview';
import { Typography } from '../../../Typography';
import { UploaderFileItemIcon } from '../UploaderFileItemIcon';

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'fileState' && prop !== 'canPreview',
})<{ fileState: 'success' | 'loading' | 'error'; canPreview: boolean }>(({ theme, fileState, canPreview }) => ({
  margin: '8px 2px',
  padding: '16px',
  borderRadius: '12px',
  backgroundColor: '#FFF',
  height: '100%',
  outline:
    fileState === 'error'
      ? `2px solid ${theme.custom?.colours?.error600}`
      : `1px solid ${theme.custom?.colours?.grey200}`,
  cursor: canPreview && fileState === 'success' ? 'pointer' : 'default',
  '&:hover':
    canPreview && fileState === 'success'
      ? {
          outline: `2px solid ${theme.palette.primary.main}`,
        }
      : {},
  '&:focus':
    canPreview && fileState === 'success'
      ? {
          outline: `2px solid ${theme.palette.primary.main}`,
          border: 'none',
        }
      : {},
}));

const PrimaryText = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 500,
  color: theme.custom?.colours?.grey700,
  wordBreak: 'break-all',
}));

const SecondaryText = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  color: theme.custom?.colours?.grey600,
}));

export interface UploaderFileItemProps {
  /**
   * The id prefix used for testing
   */
  idPrefix: string;
  /**
   * The translation function
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * If the file remove button is disabled
   */
  removeBtnDisabled?: boolean;
  /**
   * Whether the progress indicator is showing
   */
  isShowingProgressIndicator?: boolean;
  /**
   * The file object
   */
  file: Media;
  /**
   * Whether the remove file button is shown, by default it is true
   */
  isRemoveFileBtnVisible: boolean;
  /**
   * Whether the file can be previewed, by default it is true
   */
  canPreview: boolean;
  /**
   * Hides the upload-state label (e.g. "File uploaded") in the secondary text.
   * Use for read-only contexts where the file is already persisted and the
   * state wording would be misleading. By default the label is shown.
   */
  hideStateLabel?: boolean;
  /**
   * When true, image files render a thumbnail of the image instead of the
   * generic file-type icon. Non-image files keep the type icon. Defaults to
   * false so existing uploaders are unaffected.
   */
  showImageThumbnail?: boolean;
}

export const UploaderFileItem = ({
  idPrefix,
  t,
  file,
  removeBtnDisabled,
  isShowingProgressIndicator = true,
  isRemoveFileBtnVisible = true,
  canPreview = true,
  hideStateLabel = false,
  showImageThumbnail = false,
}: UploaderFileItemProps): React.ReactNode => {
  const theme = useTheme();

  const translations = {
    loading: t('common:file_uploader:file_item:loading'),
    error: t('common:file_uploader:file_item:error'),
    success: t('common:file_uploader:file_item:success'),
    deleteAction: t('common:file_uploader:file_item:delete'),
    filePreviewAction: t('common:file_uploader:file_item:preview_file'),
  };

  if (!file.state) {
    return null;
  }

  const secondaryText = ['size' in file ? `${file.size} KB` : '', hideStateLabel ? '' : translations[file.state]]
    .filter(Boolean)
    .join(' ');

  const fileType = 'nameAndType' in file ? file?.nameAndType?.type : undefined;
  const imageUrl = file.tempPath || file.path || file.file?.filePath;
  const showThumbnail =
    showImageThumbnail && file.state === 'success' && !!imageUrl && !!fileType && imageArray.includes(fileType);

  return (
    <StyledListItem
      aria-label={canPreview ? translations['filePreviewAction'] : undefined}
      fileState={file.state}
      canPreview={canPreview}
      tabIndex={canPreview && file.state === 'success' ? 0 : -1}
      onKeyDown={(e) => {
        if (canPreview && e.key === 'Enter') {
          file.state === 'success' && file.setFilePreview?.();
        }
      }}
      onClick={() => canPreview && file.state === 'success' && file.setFilePreview?.()}
      data-testid={`${idPrefix}-item`}
      secondaryAction={
        <Stack direction="row" spacing={5} alignItems="center">
          {isRemoveFileBtnVisible && (file.state === 'success' || file.state === 'error') && (
            <IconButton
              disabled={removeBtnDisabled}
              edge="end"
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                file.handleRemove?.();
              }}
              aria-label={`${translations['deleteAction']} ${
                file.state === 'error' ? file.name : `${file.nameAndType?.name}.${file.nameAndType?.type}`
              }`}
            >
              <RevampedIcon
                fullPath="/media/icons/design-system/file-upload/bin-icon/24px.svg"
                iconSize={RevampedIconSize.MD}
                firstPathStroke={
                  file.state === 'error' ? theme.custom?.colours?.error700 : theme.custom?.colours?.grey600
                }
              />
            </IconButton>
          )}
          {isShowingProgressIndicator &&
            (file.state === 'loading' ? (
              <CircularProgress color={'primary'} size={32} thickness={4} aria-label={translations[file.state]} />
            ) : null)}
        </Stack>
      }
    >
      <ListItemIcon sx={{ minWidth: 0, marginRight: '12px' }}>
        {showThumbnail ? (
          <Box sx={{ width: 40, height: 40, borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
            <ThumbnailPreview filePath={imageUrl as string} fileType={fileType} imgAlt={file.nameAndType?.name} />
          </Box>
        ) : (
          <UploaderFileItemIcon fileType={fileType} />
        )}
      </ListItemIcon>
      <ListItemText
        disableTypography={true}
        sx={{
          margin: 0,
          marginRight: '34px',
        }}
        primary={
          <PrimaryText>
            {file.state === 'error' ? file.name : `${file.nameAndType?.name}.${file.nameAndType?.type}`}
          </PrimaryText>
        }
        secondary={secondaryText ? <SecondaryText>{secondaryText}</SecondaryText> : null}
      />
    </StyledListItem>
  );
};
